import type { Column } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Fragment, useCallback, useMemo, useReducer } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

interface Entry extends Record<PropertyKey, unknown> {
  id: string
  display?: string | null
  key?: string | null
  group_id?: string | null
  entity?: string | null
  organization?: string | null
  self_enrollment?: SelfEnrollment | null
  attributes?: string | null
  compassion_status?: CompassionStatus | null
  cat_status?: string | null
  status?: Status | null
  client_narus_status?: string | null
  diff?: Array<{
    name: string
    client?: string | null
    profile?: string | null
  }> | null
}

const enum SelfEnrollment {
  Allowed = 'Allowed',
  Forbidden = 'Forbidden'
}

const enum NarusStatus {
  Active = 'Active',
  ActiveNoOutreach = 'Active - No Outreach',
  ActivePendingTermination = 'Active - Pending Termination',
  Cancelled = 'Cancelled',
  NotANarusClient = 'Not a Narus Client',
  Terminated = 'Terminated',
}

const enum CompassionStatus {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
  Absent = 'Absent'
}

const enum Status {
  Current = 'Current',
  Ignored = 'Ignored',
  Inconsistent = 'Inconsistent',
  Orphaned = 'Orphaned'
}

type QueryKey = [
  'clients',
  SearchParams
]

interface QueryData {
  data: Entry[]
  total: number
  count: number
}

interface State {
  searchParams: SearchParams
}

type Action =
  | { type: 'query/set', payload: string }
  | { type: 'filters/set', payload: SearchParams['filters'] }
  | { type: 'filters/merge', payload: Partial<SearchParams['filters']> }
  | { type: 'filters/clear' }
  | { type: 'sort/set', payload: Sort }
  | { type: 'pagination/page/set', payload: number }
  | { type: 'pagination/page-size/set', payload: number }

interface SearchParams {
  query: string

  filters: {
    self_enrollment: SelfEnrollment[]
    compassion_status: CompassionStatus[]
    status: Status[]
    client_narus_status: NarusStatus[]
  }

  sort: Sort
  pagination: Pagination
}

let sortKeys = [
  'display',
  'key',
  'group_id',
  'entity',
  'organization',
  'self_enrollment',
  'compassion_status',
  'cat_status',
  'status'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  page: number
  pageSize: number
}

let initialState: State = {
  searchParams: {
    query: '',
    filters: {
      self_enrollment: [],
      compassion_status: [],
      status: [],
      client_narus_status: []
    },
    sort: {
      field: 'display',
      direction: 'asc'
    },
    pagination: {
      page: 0,
      pageSize: 10
    }
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/merge': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: {
            ...state.searchParams.filters,
            ...action.payload
          },
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          filters: initialState.searchParams.filters,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            pageSize: action.payload,
            page: 0
          }
        }
      }
    }

    default: {
      return state
    }
  }
}

function isDefaultSearchParams (searchParams: Pick<SearchParams, 'query' | 'filters'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query &&
    searchParams.filters.self_enrollment.length === initialState.searchParams.filters.self_enrollment.length &&
    searchParams.filters.self_enrollment.every(v => initialState.searchParams.filters.self_enrollment.includes(v)) &&
    searchParams.filters.compassion_status.length === initialState.searchParams.filters.compassion_status.length &&
    searchParams.filters.compassion_status.every(v => initialState.searchParams.filters.compassion_status.includes(v)) &&
    searchParams.filters.client_narus_status.length === initialState.searchParams.filters.client_narus_status.length &&
    searchParams.filters.client_narus_status.every(v => initialState.searchParams.filters.client_narus_status.includes(v)) &&
    searchParams.filters.status.length === initialState.searchParams.filters.status.length &&
    searchParams.filters.status.every(v => initialState.searchParams.filters.status.includes(v))
  )
}

export function ClientsPage (): JSX.Element {
  let [ state, dispatch ] = useReducer(reducer, initialState, state => ({
    ...state,
    searchParams: {
      ...state.searchParams,
      filters: {
        ...state.searchParams.filters,
        from: new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().substring(0, 10),
        to: new Date().toISOString().substring(0, 10)
      }
    }
  }))

  let [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  let query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'clients',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn ({ queryKey }) {
      let [ , searchParams ] = queryKey
      let { query, filters, sort, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let sp = new URLSearchParams([
        [ 'count', pagination.pageSize.toString() ],
        [ 'page', pagination.page.toString() ],
        [ 'on', sort.field ],
        [ 'dir', sort.direction ],
        ...filters.self_enrollment.map(v => [ 'filters.self_enrollment', v ]),
        ...filters.compassion_status.map(v => [ 'filters.compassion_status', v ]),
        ...filters.client_narus_status.map(v => ['filters.client_narus_status', v]),
        ...filters.status.map(v => [ 'filters.status', v ]),
        ...(normalizedQuery.length > 0 ? [[ 'search', normalizedQuery ]] : [])
      ])

      let response = await http.getRequest(`/admin/clients?${sp.toString()}`)
      return {
        ...response,
        data: response.data.map((item: Entry) => ({
          ...item,
          id: item.key ?? ''
        }))
      }
    },

    placeholderData: keepPreviousData
  })

  let columns = useMemo<Array<Column<Entry>>>(() => [
    {
      name: 'display',
      header: 'Display',
      isSortable: true
    },
    {
      name: 'key',
      header: 'Key',
      isSortable: true
    },
    {
      name: 'group_id',
      header: 'Group',
      isSortable: true
    },
    {
      name: 'entity',
      header: 'Entity',
      isSortable: true
    },
    {
      name: 'organization',
      header: 'Organization',
      isSortable: true
    },
    {
      name: 'self_enrollment',
      header: 'Self Enrollment',
      isSortable: true
    },
    {
      name: 'compassion_status',
      header: 'Compassion Status',
      isSortable: true
    },
    {
      name: 'cat_status',
      header: 'CAT Status',
      isSortable: true
    },
    {
      name: 'status',
      header: 'Status',
      isSortable: true
    },
    {
      name: 'client_narus_status',
      header: 'Narus Status',
      isSortable: true
    },
    {
      name: 'attributes',
      header: 'Attributes',
      isSortable: false
    },
    {
      name: 'client_tags',
      header: 'Compassion View',
      isSortable: false
    }
  ], [])

  let filters = useMemo<Table.Filter[]>(() => {
    return [
      {
        name: 'status',
        value: state.searchParams.filters.status,
        options: [
          { value: Status.Current },
          { value: Status.Ignored },
          { value: Status.Inconsistent },
          { value: Status.Orphaned }
        ]
      },
      {
        name: 'self_enrollment',
        value: state.searchParams.filters.self_enrollment,
        options: [
          { value: SelfEnrollment.Allowed },
          { value: SelfEnrollment.Forbidden }
        ]
      },
      {
        name: 'compassion_status',
        value: state.searchParams.filters.compassion_status,
        options: [
          { value: CompassionStatus.Enabled },
          { value: CompassionStatus.Disabled },
          { value: CompassionStatus.Absent }
        ]
      },
      {
        name: 'client_narus_status',
        value: state.searchParams.filters.client_narus_status,
        options: [
          {value: NarusStatus.Active},
          {value: NarusStatus.ActiveNoOutreach},
          {value: NarusStatus.ActivePendingTermination},
          {value: NarusStatus.Cancelled},
          {value: NarusStatus.NotANarusClient},
          {value: NarusStatus.Terminated},
        ]
      }
    ]
  }, [
    state.searchParams.filters.status,
    state.searchParams.filters.self_enrollment,
    state.searchParams.filters.compassion_status,
    state.searchParams.filters.client_narus_status
  ])

  let handleFilterChange = useCallback(<T extends keyof Entry>(name: T, value: Array<Entry[T]>): void => {
    dispatch({
      type: 'filters/merge',
      payload: {
        [name]: value
      }
    })
  }, [])

  let handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })
  }, [])

  let handleSortChange = useCallback((field: keyof Entry, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  let handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  let handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  let pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.total ?? 0,
      totalUnfiltered: query.data?.total ?? 0
    }
  }, [
    state.searchParams.pagination,
    query.data
  ])

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Clients</span>
        </h1>

        <SearchInput
          value={state.searchParams.query}
          placeholder='Search by client, group, entity, organization, status, attributes, etc.'
          onChange={event => {
            dispatch({
              type: 'query/set',
              payload: event.target.value
            })
          }}
        />
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        noDataPlaceholder='No records found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        expandingComponent={(item, state, setState) => {
          if (!(item.diff?.length ?? 0)) {
            return null
          }

          return (
            <ExpandingComponent item={item} />
          )
        }}
      />
    </div>
  )
}

let keyMapper: Record<string, string> = {
  display: 'Display',
  group_id: 'Group',
  entity: 'Entity',
  self_enrollment: 'Self Enrollment',
  attributes: 'Attributes',
  cat_status: 'CAT Status'
}

interface ExpandingComponentProps {
  item: Entry
}

function ExpandingComponent ({ item }: ExpandingComponentProps): JSX.Element | null {
  if (!item.diff?.length) {
    return null
  }

  return (
    <div className={styles.expandableRowContent}>
      <div className={styles.row}>
        <div className={styles.details}>
          {item.diff.map((diff, index) => (
            <Fragment key={diff.name}>
              <span className={styles.label}>
                {keyMapper[diff.name] ?? diff.name}
              </span>
              <span className={styles.value}>
                Compassion:&nbsp;&nbsp;&nbsp;<span className={styles.modifiedValue}>{diff.client ?? ''}</span>
                &nbsp;&nbsp;&nbsp;
                CAT:&nbsp;&nbsp;&nbsp;<span className={styles.modifiedValue}>{diff.profile ?? ''}</span>
              </span>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}
